import { useCallback, useEffect, useState } from "react";
import Layout from "../../components/Layout/Layout";
import FaleAPI from "fale";
import { useDebounce } from "../../hooks/debounce";
import TabbedTable from "../../components/Layout/TabbedTable";
import { useDropzone } from "react-dropzone";
import OverlayTrigger from "react-bootstrap/esm/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";

function Dishes(props) {
  const draftTab = {
    label: "Hidden",
    statuses: ["Draft"],
    color: "danger",
    title: "These dishes are hidden from the public",
  };
  const tabs = [
    {
      label: "Live",
      statuses: ["Live"],
      color: "success",
      title: "These dishes are shown to the public",
    },
    draftTab,
  ];
  const [dishes, setDishes] = useState(null);
  const [selectedTab, setSelectedTab] = useState(tabs[0]);
  const [statusCounts, setStatusCounts] = useState([]);
  const [fileUploading, setFileUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState(0);
  const [fileKey, setFileKey] = useState("");
  const [showDishForm, setShowDishForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingDishes, setLoadingDishes] = useState({});
  const [dishUpdating, setDishUpdating] = useState(null);

  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    console.log();

    setFileUploading(true);

    const data = await FaleAPI.files.upload(file, "product");
    setFileKey(data.key);
    setFile(file);

    setFileUploading(false);
  }, []);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const debounceRequest = useDebounce(() => {
    if (props.selectedKitchen) {
      FaleAPI.products
        .getForKitchen(props.selectedKitchen?.uuid, selectedTab.statuses)
        .then((data) => {
          setStatusCounts(data.counts);
          setDishes(data.products);
        });
    }
  });

  const saveDish = () => {
    setSaving(true);
    FaleAPI.products
      .save(props.selectedKitchen?.uuid, {
        id: dishUpdating?.id,
        name,
        description,
        file: fileKey,
        price,
      })
      .then(() => {
        setSaving(false);
        setShowDishForm(false);
        if (dishUpdating) {
          debounceRequest();
        } else {
          setSelectedTab(draftTab);
        }
        setDishUpdating(null);
        setName("");
        setFile(null);
        setDescription("");
        setPrice(0);
        setFileKey("");
      });
  };

  useEffect(() => {
    debounceRequest();
  }, [props.selectedKitchen, selectedTab, debounceRequest]);

  const columns = [
    {
      label: "Image",
      value: (product) => (
        product.image ? <img
          src={`https://falenz.s3.ap-southeast-2.amazonaws.com/${product.image}`}
          width="100"
          alt="product"
        /> : "No image"
      ),
    },
    { label: "Name", value: (product) => product.name },
    {
      label: "Description",
      value: (product) => (
        <div className="badge rounded-pill">
          <OverlayTrigger
            overlay={<Tooltip placement="top">{product.description}</Tooltip>}
          >
            <span style={{ cursor: "pointer" }}>Description</span>
          </OverlayTrigger>
        </div>
      ),
    },
    { label: "Price", value: (product) => product.price },
    {
      label: "",
      value: (product) => (
        <>
          {product.status !== "Live" && (
            <button
              className={`btn btn-success btn-sm rounded`}
              onClick={async () => {
                setLoadingDishes({ ...loadingDishes, [product.id]: true });
                await FaleAPI.products.goLive(product.id);
                setLoadingDishes({ ...loadingDishes, [product.id]: false });
                debounceRequest();
              }}
            >
              Go Live
            </button>
          )}

          {product.status !== "Draft" && (
            <button
              className={`btn btn-danger btn-sm rounded`}
              onClick={async () => {
                setLoadingDishes({ ...loadingDishes, [product.id]: true });
                await FaleAPI.products.makeDraft(product.id);
                setLoadingDishes({ ...loadingDishes, [product.id]: false });
                debounceRequest();
              }}
            >
              Hide
            </button>
          )}

          <button
            className={`btn btn-info btn-sm rounded ms-2`}
            onClick={async () => {
              setDishUpdating(product);
              setName(product.name);
              setDescription(product.description);
              setPrice(product.price);
              setShowDishForm(true);
              setFileKey(product.image);
              window.scrollTo(0, 0)
            }}
          >
            Update
          </button>
        </>
      ),
    },
  ];

  return (
    <Layout {...props}>
      <div className="row gutters">
        <div className="col-xl-12 col-lg-12 col-md-12 col-sm-12 col-12">
          <h4>Dishes</h4>
          <button
            className="btn btn-primary mt-4 rounded"
            onClick={() => {
              setShowDishForm(true);
            }}
          >
            Add New Dish
          </button>

          {showDishForm ? (
            <div className="card mt-2">
              <div className="card-header">
                <div className="card-title">
                  {dishUpdating ? "Updating" : "New"} Dish
                </div>
              </div>
              <div className="card-body">
                <div className="row gutters">
                  <div className="col-xl-4 col-lg-4 col-md-4 col-sm-4 col-12">
                    <div className="field-wrapper">
                      <input
                        className="form-control text-dark"
                        type="text"
                        value={name}
                        onChange={(event) => {
                          setName(event.target.value);
                        }}
                      />
                      <div className="field-placeholder">
                        Name <span className="text-danger">*</span>
                      </div>
                    </div>
                    <div className="field-wrapper">
                      <input
                        className="form-control text-dark"
                        type="text"
                        value={description}
                        onChange={(event) => {
                          setDescription(event.target.value);
                        }}
                      />
                      <div className="field-placeholder">
                        Description <span className="text-danger">*</span>
                      </div>
                    </div>
                    <div className="field-wrapper">
                      <input
                        className="form-control text-dark"
                        type="text"
                        value={price}
                        onChange={(event) => {
                          setPrice(event.target.value);
                        }}
                      />
                      <div className="field-placeholder">
                        Price <span className="text-danger">*</span>
                      </div>
                    </div>

                    <div className="mb-4">
                      <p className="text-success mb-2">
                        {file
                          ? `${file?.name} has been uploaded successfully!`
                          : null}
                      </p>
                      <div
                        {...getRootProps({
                          className: "dropzone",
                        })}
                      >
                        <input {...getInputProps()} />
                        {isDragActive ? (
                          <p style={{ padding: "20px" }}>
                            Drop the photo here ...
                          </p>
                        ) : (
                          <p style={{ padding: "20px" }}>
                            {fileUploading
                              ? "Your file is uploading..."
                              : "Tap, Click or Drag n Drop a photo of the dish here"}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-xl-12 col-lg-12 col-md-12 col-sm-12 col-12">
                    <button
                      className="btn btn-danger rounded"
                      onClick={() => {
                        setShowDishForm(false);
                        setDishUpdating(null);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-success ms-2 rounded"
                      onClick={() => {
                        saveDish();
                      }}
                    >
                      {saving ? "Saving" : "Save"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          <TabbedTable
            type="Dish"
            data={dishes}
            tabs={tabs.map((t) => ({ ...t, columns }))}
            rowIsLoading={(dish) => loadingDishes[dish.id]}
            selectedTab={selectedTab}
            onTabClick={(tab) => () => {
              setDishes(null);
              setSelectedTab(tab);
              debounceRequest();
            }}
            getTabBadgeCount={(tab) =>
              statusCounts
                ?.filter?.((c) => tab.statuses.includes(c.status))
                .reduce?.(
                  (count, status) => count + parseInt(status.productCount),
                  0
                )
            }
          />
        </div>
      </div>
    </Layout>
  );
}

export default Dishes;
